import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";

import { useActiveTrackPlayback } from "../audio-preview/core/activeTrack";
import GenerativeAudioThumbnail from "../media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import ToolTip from "../tooltip/ToolTip";

/**
 * 円形オーディオギャラリー（`pages/free-sound-assets` の円形展示 UI のグローバル化）。
 *
 * 音源を円ボタンとして円周上に等間隔配置し、ゆっくり回転させる。円の中身は
 * サムネイルグリッドと同じ流儀（ジャケット画像があれば画像、無ければ音源から
 * 自動生成する `GenerativeAudioThumbnail`）で描く。
 *
 * 再生はこのコンポーネントでは行わない。クリックで閃光エフェクトを出しつつ
 * `onItemClick` を呼ぶので、呼び出し側がフローティングプレイヤーバー
 * （`AudioPlayerBar`）等を開く。バーの再生状態は `activeTrack` 経由で受け取り、
 * 再生中の生成サムネイルは再生位置に合わせて動き続ける。
 *
 * - 親要素の幅に追従する正方形レイアウト（LP 版の全画面 fixed とは異なり、
 *   ページセクション内に埋め込める）
 *
 * 利用箇所: 公開プロフィール（作品展示方法 = サークル表示）。
 * ※ `pages/free-sound-assets` の LP は期間限定（削除予定）のためこのコンポーネント
 *   へは移行しない。
 */

export interface CircularAudioGalleryItem {
  id: string;
  title: string;
  /** 音源 URL。生成サムネイルの種と `activeTrack` の照合キーになる。 */
  src: string;
  /** ジャケット画像 URL。未指定なら一覧と同じ生成サムネイルを表示。 */
  thumbnailSrc?: string;
  /** 生成サムネイル用。波形 peaks JSON の URL（動きの駆動信号になる）。 */
  waveformUrl?: string | null;
  /** peaks キャッシュのキー。波形プレイヤーと共有できる ID があれば渡す。 */
  cacheKey?: string | null;
}

export interface CircularAudioGalleryProps {
  items: CircularAudioGalleryItem[];
  /** 円ボタンのクリック時に呼ばれる（再生の開始は呼び出し側が行う）。 */
  onItemClick?: (_item: CircularAudioGalleryItem) => void;
  /** 回転 1 周の秒数。既定 120。 */
  rotationDuration?: number;
  className?: string;
  style?: React.CSSProperties;
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const flashAnimation = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.3);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.4);
    opacity: 0;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const RotatingRing = styled.div<{ $duration: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  animation: ${rotate} ${(props) => props.$duration}s linear infinite;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
`;

interface CircleButtonProps {
  $size: number;
  $x: number;
  $y: number;
}

/**
 * 円ボタンの枠。見た目（画像 / 生成サムネイル）は子に任せ、ここは配置と
 * 円形クリップ・ホバー時の持ち上がりだけを担当する。
 */
const CircleButton = styled.button<CircleButtonProps>`
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--soremo-border);
  overflow: hidden;
  position: absolute;
  padding: 0;
  background: var(--soremo-background);

  /* モバイルでのタップ時の選択状態を防ぐ */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  user-select: none;
  touch-action: manipulation;
  will-change: transform;

  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  left: calc(50% + ${(props) => props.$x}px - ${(props) => props.$size / 2}px);
  top: calc(50% + ${(props) => props.$y}px - ${(props) => props.$size / 2}px);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(1.04);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px var(--soremo-blue);
  }
`;

const CircleImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

/** 生成サムネイルの置き場。中身（canvas）は absolute で敷かれる。 */
const CircleGenerative = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  overflow: hidden;
`;

/** 閃光の表示時間。CSS アニメと、state から取り除くタイマーで共用する。 */
const FLASH_DURATION_MS = 600;

/** クリック時の白い閃光（サムネイル色に依存しない演出に統一）。 */
const FlashEffect = styled.div<{ $x: number; $y: number; $size: number }>`
  position: absolute;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  left: calc(50% + ${(props) => props.$x}px);
  top: calc(50% + ${(props) => props.$y}px);
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 100;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.95) 0%,
      transparent 70%
    );
    border-radius: 50%;
    animation: ${flashAnimation} ${FLASH_DURATION_MS}ms
      cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    box-shadow:
      0 0 40px rgba(255, 255, 255, 0.8),
      0 0 80px rgba(255, 255, 255, 0.6);
  }
`;

interface FlashEffectItem {
  id: string;
  x: number;
  y: number;
}

/**
 * 円ボタン 1 個。フローティングプレイヤーバーの再生状態（activeTrack）を
 * 購読し、生成サムネイルをバーの再生位置に合わせて動かす。
 */
const GalleryCircleButton: React.FC<{
  item: CircularAudioGalleryItem;
  size: number;
  x: number;
  y: number;
  onClick: () => void;
}> = ({ item, size, x, y, onClick }) => {
  const [isHovering, setIsHovering] = useState(false);
  const { isPlaying, getCurrentTime, readSpectrum } = useActiveTrackPlayback(
    item.src,
  );

  return (
    <ToolTip description={item.title}>
      <CircleButton
        type="button"
        $size={size}
        $x={x}
        $y={y}
        onClick={onClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        aria-label={`${item.title} を再生`}
      >
        {item.thumbnailSrc ? (
          <CircleImage src={item.thumbnailSrc} alt="" />
        ) : (
          <CircleGenerative>
            <GenerativeAudioThumbnail
              src={item.src}
              waveformUrl={item.waveformUrl}
              cacheKey={item.cacheKey}
              isPlaying={isPlaying}
              isHovering={isHovering}
              getCurrentTime={getCurrentTime}
              readSpectrum={readSpectrum}
            />
          </CircleGenerative>
        )}
      </CircleButton>
    </ToolTip>
  );
};

const CircularAudioGallery: React.FC<CircularAudioGalleryProps> = ({
  items,
  onItemClick,
  rotationDuration = 120,
  className,
  style,
}) => {
  const [flashEffects, setFlashEffects] = useState<FlashEffectItem[]>([]);
  const [containerSize, setContainerSize] = useState(0);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // 親要素の幅に追従（LP 版の window 実測からの置き換え）。
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setContainerSize(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 円周上の等間隔配置。ボタン径は基準 8% とし、アイテムが多い場合は隣接間の
  // 弦長に収まるよう縮小して重なりを防ぐ。
  const layout = useMemo(() => {
    const radius = containerSize * 0.4375;
    const count = items.length;
    const chord =
      count > 1
        ? 2 * radius * Math.sin(Math.PI / count)
        : Number.POSITIVE_INFINITY;
    const buttonSize = Math.max(
      16,
      Math.min(containerSize * 0.08, chord * 0.9),
    );
    const angleStep = 360 / Math.max(1, count);
    const positions = items.map((item, index) => {
      const angle = index * angleStep;
      const x = Math.cos((angle * Math.PI) / 180) * radius;
      const y = Math.sin((angle * Math.PI) / 180) * radius;
      return { item, x, y };
    });
    return { buttonSize, positions };
  }, [items, containerSize]);

  const handleCircleClick = useCallback(
    (item: CircularAudioGalleryItem, x: number, y: number) => {
      const flashId = `flash-${Date.now()}-${Math.random()}`;
      setFlashEffects((prev) => [...prev, { id: flashId, x, y }]);
      window.setTimeout(() => {
        setFlashEffects((prev) => prev.filter((flash) => flash.id !== flashId));
      }, FLASH_DURATION_MS);

      onItemClick?.(item);
    },
    [onItemClick],
  );

  if (items.length === 0) return null;

  const flashSize = Math.min(120, Math.max(64, containerSize * 0.15));

  return (
    <Wrapper ref={wrapperRef} className={className} style={style}>
      <RotatingRing $duration={rotationDuration}>
        {containerSize > 0 &&
          layout.positions.map(({ item, x, y }) => (
            <GalleryCircleButton
              key={item.id}
              item={item}
              size={layout.buttonSize}
              x={x}
              y={y}
              onClick={() => handleCircleClick(item, x, y)}
            />
          ))}

        {flashEffects.map((flash) => (
          <FlashEffect
            key={flash.id}
            $x={flash.x}
            $y={flash.y}
            $size={flashSize}
          />
        ))}
      </RotatingRing>
    </Wrapper>
  );
};

export default CircularAudioGallery;
